import React, { useEffect, useState, useContext } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { axios } from "../axios";

import { AccessContext } from "@/contexts";
import { LogoutButton } from "@/components/Logout";
import { Screen } from "@/components/Screen";
import { LoginWelcome } from "@/components/Login";
import { Callout, DHead, Fig, Foot, Lab, Tag } from "@/components/ms";
import { usePlan, useProfile } from "@moonscape/shared";

const APP_VERSION = "24.06.30.0";

/** Mono initials disc — the mock's account mark. */
const Avatar = ({ email, name }: { email?: string; name?: string }) => {
  const source = (name || email || "?").trim();
  const initials = source.includes("@")
    ? source.slice(0, 2).toUpperCase()
    : source
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();
  return (
    <span className="grid h-[46px] w-[46px] flex-none place-items-center rounded-full border border-ms-line bg-ms-card2 font-mono text-[15px] font-semibold text-ms-ink">
      {initials}
    </span>
  );
};

export const Profile = () => {
  const accessToken = useContext(AccessContext);
  const [response, setResponse] = useState("");
  const { user, isAuthenticated, isLoading } = useAuth0();

  const { data: plan } = usePlan({});
  const { isAccountant, updateAccountantStatus } = useProfile();
  const [isTogglingAccountant, setIsTogglingAccountant] = useState(false);

  const getPositionData = () => {
    //
  };

  useQuery({
    queryKey: ["user"],
    queryFn: getPositionData,
  });

  // fetch positions
  useEffect(() => {
    async function getData() {
      try {
        const resp = await axios.get(`/positions`);
        setResponse(resp.data.message);
      } catch (e) {
        if (e instanceof Error && e.message === "Consent required") {
          setResponse("Please verify your account by checking your email.");
        }
      }
    }

    if (!isLoading) {
      getData();
    }
  }, [accessToken, isAuthenticated, isLoading]);

  if (isLoading) {
    return (
      <Screen>
        <Lab>Loading your account…</Lab>
      </Screen>
    );
  }

  if (!isAuthenticated) {
    return (
      <Screen>
        <LoginWelcome />
      </Screen>
    );
  }

  return (
    <div className="mt-[var(--gap)] mb-16">
      <div className="grid items-start gap-[var(--gap)] ms1100:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-[var(--gap)]">
          {/* Identity */}
          <div className="flex items-center gap-3.5">
            <Avatar email={user?.email} name={user?.name} />
            <div className="min-w-0">
              <div className="truncate text-[19px] font-semibold tracking-[-0.025em]">
                {user?.email}
              </div>
              <div className="mt-[5px] flex flex-wrap items-center gap-2">
                {plan?.planName && <Tag tone="pos">{plan.planName}</Tag>}
                <Tag tone={isAccountant ? "pos" : "neu"}>
                  {isAccountant ? "Tax professional" : "Personal"}
                </Tag>
              </div>
            </div>
          </div>

          {/* Plan */}
          <section>
            <DHead label="Plan" right="Purchases" />
            <div className="mt-1">
              <Fig
                label={plan?.planName || "No plan yet"}
                sub={
                  plan?.expiredAt
                    ? `Expires ${new Date(plan.expiredAt).toDateString()}`
                    : "One payment, one tax year"
                }
                tag={
                  <Link to="/plans">
                    <Tag tone="pos">Change</Tag>
                  </Link>
                }
              />
              <Fig
                label="Moonscape Concierge"
                sub="A person reviews your pack"
                value="From £499"
                tone="green"
              />
            </div>
          </section>

          {/* Account */}
          <section>
            <DHead label="Account" right="This profile" />
            <div className="mt-1">
              <Fig label="Name" value={user?.name || "—"} />
              <Fig label="Email" value={user?.email || "—"} />
              <Fig label="Account id" sub="Auth0 subject" value={user?.sub || "—"} />
              <Fig
                label="Account type"
                sub={
                  isAccountant
                    ? "Client management is on"
                    : "Client management is off"
                }
                tag={
                  isAccountant ? (
                    <button
                      type="button"
                      disabled={isTogglingAccountant}
                      onClick={async () => {
                        if (
                          !window.confirm(
                            "Switch to a Personal account? You'll lose access to client management features."
                          )
                        )
                          return;
                        setIsTogglingAccountant(true);
                        try {
                          await updateAccountantStatus(false);
                        } finally {
                          setIsTogglingAccountant(false);
                        }
                      }}
                      className="disabled:opacity-50"
                    >
                      <Tag tone="neu">
                        {isTogglingAccountant ? "Switching…" : "Switch to Personal"}
                      </Tag>
                    </button>
                  ) : undefined
                }
                value={isAccountant ? undefined : "Personal"}
              />
              {response && <Fig label="Account status" sub={response} value="Notice" tone="tan" />}
            </div>
            {/* CPA accounts are created via invite/quiz only — no self-serve
                upgrade here. Personal users kept mistaking this for a paid tier. */}
          </section>

          {/* Tax settings */}
          <section>
            <DHead
              label="Tax settings"
              right={<span className="text-ms-tan">Changes your figures</span>}
            />
            <div className="mt-1">
              <Fig
                label="Jurisdiction, method and band"
                sub="Where you file and how gains are matched"
                tag={
                  <Link to="/settings/tax">
                    <Tag tone="neu">Open</Tag>
                  </Link>
                }
              />
              <Fig
                label="Tax timezone and notifications"
                sub="How dates are read, what we email you"
                tag={
                  <Link to="/settings/general">
                    <Tag tone="neu">Open</Tag>
                  </Link>
                }
              />
            </div>
          </section>

          {/* Legal */}
          <section>
            <DHead label="Legal" />
            <div className="mt-1">
              <Fig
                label="Terms of service"
                tag={
                  <a
                    href="https://moonscape.app/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Tag tone="neu">Read</Tag>
                  </a>
                }
              />
              <Fig
                label="Privacy policy"
                tag={
                  <a
                    href="https://moonscape.app/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Tag tone="neu">Read</Tag>
                  </a>
                }
              />
              <Fig
                label="Support"
                sub="Usually answered within a day"
                tag={
                  <Link to="/support">
                    <Tag tone="neu">Contact</Tag>
                  </Link>
                }
              />
            </div>
          </section>
        </div>

        {/* Session rail */}
        <aside className="flex flex-col gap-[var(--gap)] ms1100:sticky ms1100:top-[76px]">
          <section>
            <DHead label="Session" right="This device" />
            <div className="mt-1">
              <Fig label="Signed in" sub="Your data stays if you sign out" value="Active" />
              <Fig
                label={<span className="text-ms-neg">Delete account</span>}
                sub="Permanent — every source and report"
                tag={
                  <a href="mailto:hello@moonscape.app?subject=Delete my account">
                    <Tag tone="neg">Request</Tag>
                  </a>
                }
              />
            </div>
            <div className="mt-3.5">
              <LogoutButton />
            </div>
          </section>

          <Callout tone="tan">
            <b>Before deleting.</b> Download your pack first — a paid report is not
            reissued on a new account, and backups are purged within 30 days.
          </Callout>
        </aside>
      </div>

      <Foot className="mt-[var(--gap)] px-0" right={`v${APP_VERSION}`}>
        Keys are encrypted at rest. Every figure resolves to a source record you can
        export.
      </Foot>
    </div>
  );
};
